import React from "react";

import { compact, isEmpty, map } from "lodash/fp";
import { Duration } from "luxon";
import { useParams } from "react-router-dom";

import type { SxProps } from "@mui/material";

import Chapter from "@app/analytics/MeetingReport/components/Chapter";
import UpsellOverlay from "@app/analytics/MeetingReport/components/UpsellOverlay";
import useReportActions from "@app/analytics/MeetingReport/hooks/useReportActions";
import useSessionMetadata from "@app/analytics/MeetingReport/hooks/useSessionMetadata";
import useVideoStore from "@app/analytics/MeetingReport/stores/video";
import * as Events from "@app/analytics/MeetingReport/tracking/events";
import usePreloadImages from "@app/common/hooks/usePreloadImages";
import ROUTES from "@app/common/routes";
import type { SessionChapter } from "@app/common/types";
import { getAppUrl } from "@app/utils/utils";

import {
  EmptyStateContainer,
  EmptyStateSubtext,
  EmptyStateTitle,
} from "@rui/EmptyStateContainer";
import { Column } from "@rui/Stack";

const MeetingReportChapters = ({
  chapters,
  sx,
}: {
  chapters: SessionChapter[];
  sx?: SxProps;
}) => {
  usePreloadImages(compact(map("thumbnailUrl", chapters)));

  const { sessionId } = useParams();
  const {
    data: { shouldUpsellTranscriptEntities: shouldUpsell },
  } = useSessionMetadata();

  const { shouldJumpToTimestamp, makeJumpToTimestamp, makeScrollToItem } =
    useReportActions();

  const playbackEnabled = useVideoStore((state) => state.playbackEnabled);

  if (shouldUpsell) {
    return (
      <Column spacing={1} sx={{ position: "relative", ...sx }}>
        <UpsellOverlay>
          {chapters.map((chapter) => (
            <Chapter
              key={chapter.id}
              title={chapter.title}
              timestamp={chapter.timestamp}
              duration={0}
            />
          ))}
        </UpsellOverlay>
      </Column>
    );
  }

  const jumpToChapter = (chapter: SessionChapter) => {
    if (shouldJumpToTimestamp) {
      return makeJumpToTimestamp(chapter.relativeStartTime, {
        onJumpTo: () =>
          Events.playChapter({ id: chapter.id, label: chapter.title }),
      });
    }

    return makeScrollToItem({ type: "chapter", id: chapter.id });
  };

  return (
    <Column spacing={1} sx={sx}>
      {isEmpty(chapters) ? (
        <EmptyStateContainer spacing={1.5} sx={{ py: 1.5 }}>
          <EmptyStateTitle>Nothing to see here</EmptyStateTitle>
          <EmptyStateSubtext>
            No chapters generated for this meeting
          </EmptyStateSubtext>
        </EmptyStateContainer>
      ) : (
        <>
          {chapters.map((chapter) => (
            <Chapter
              key={chapter.id}
              title={chapter.title}
              timestamp={chapter.timestamp}
              deepLink={
                sessionId && chapter.relativeStartTime != null
                  ? getAppUrl(
                      ROUTES.meetingReportAtTimestamp(
                        sessionId,
                        Math.floor(chapter.relativeStartTime)
                      )
                    )
                  : undefined
              }
              duration={Duration.fromMillis(
                chapter.endTime.toMillis() - chapter.startTime.toMillis()
              ).as("minutes")}
              clipSrc={playbackEnabled ? chapter.thumbnailUrl : null}
              onClick={jumpToChapter(chapter)}
            />
          ))}
        </>
      )}
    </Column>
  );
};

export default MeetingReportChapters;
